import React, { useState, useEffect } from "react";
import { Upload, X, FileText, AlertCircle, Download, Trash2, Eye } from "lucide-react";

const ServiceDocumentUploader = ({ serviceId, onDocumentUpload, onDocumentDelete, onStatusUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState("OTHER");
  const [description, setDescription] = useState("");

  const documentTypes = [
    { value: "CONTRACT", label: "Contract" },
    { value: "INVOICE", label: "Invoice" },
    { value: "RECEIPT", label: "Receipt" },
    { value: "CERTIFICATE", label: "Certificate" },
    { value: "LICENSE", label: "License" },
    { value: "TRANSLATION", label: "Translation" },
    { value: "OTHER", label: "Other" },
  ];

  // Fetch existing documents
  const fetchDocuments = async () => {
    if (!serviceId) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/services/${serviceId}/documents`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Ensure documents is always an array
        const documentsArray = Array.isArray(data.documents) ? data.documents : 
                              Array.isArray(data) ? data : [];
        setDocuments(documentsArray);
      } else {
        console.warn("Failed to fetch documents, API might not be implemented yet");
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch documents if serviceId exists
    if (serviceId) {
      fetchDocuments();
    } else {
      setDocuments([]);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchDocuments is stable
  }, [serviceId]);

  // Update service status when documents are uploaded/updated
  const updateServiceStatus = async (action) => {
    if (!serviceId || !onStatusUpdate) return;
    
    try {
      // Call the parent component's status update function
      await onStatusUpdate(action);
    } catch (error) {
      console.error("Error updating service status:", error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (15MB limit)
      if (file.size > 15 * 1024 * 1024) {
        setNotification({
          show: true,
          message: "File size exceeds 15MB limit",
          type: "error",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedFile) {
      setNotification({
        show: true,
        message: "Please select a file to upload",
        type: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("documentType", documentType);
    if (description.trim()) {
      formData.append("description", description.trim());
    }

    try {
      setUploading(true);
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/services/${serviceId}/documents`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload document");
      }

      const result = await response.json();
      
      setNotification({
        show: true,
        message: "Document uploaded successfully",
        type: "success",
      });

      // Reset form
      setSelectedFile(null);
      setDescription("");
      setDocumentType("OTHER");
      setShowUploadForm(false);
      
      // Clear file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';

      // Refresh documents list
      await fetchDocuments();

      // Update service status
      await updateServiceStatus('document_uploaded');

      // Notify parent component
      if (onDocumentUpload) {
        onDocumentUpload(result);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      
      // Handle specific MongoDB error
      if (error.message && error.message.includes("must be an array")) {
        setNotification({
          show: true,
          message: "Backend API needs to be updated to handle service documents properly. Please contact the development team.",
          type: "error",
        });
      } else {
        setNotification({
          show: true,
          message: error.message || "Failed to upload document",
          type: "error",
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentDelete = async (fileUrl, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `https://api.instacorp.ae/api/admin/data/services/${serviceId}/documents`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ fileUrl }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete document");
      }

      setNotification({
        show: true,
        message: "Document deleted successfully",
        type: "success",
      });

      // Refresh documents list
      await fetchDocuments();

      // Update service status
      await updateServiceStatus('document_deleted');

      // Notify parent component
      if (onDocumentDelete) {
        onDocumentDelete(fileUrl);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      setNotification({
        show: true,
        message: error.message || "Failed to delete document",
        type: "error",
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-4">
      {notification.show && (
        <div
          className={`p-4 rounded-md ${
            notification.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {notification.type === "success" ? (
              <FileText className="w-4 h-4 mr-2" />
            ) : (
              <AlertCircle className="w-4 h-4 mr-2" />
            )}
            {notification.message}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Service Documents</h3>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </button>
      </div>

      {showUploadForm && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the document"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File
              </label>
              <input
                id="file-input"
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT (Max 15MB)
              </p>
            </div>

            {selectedFile && (
              <div className="bg-white p-3 rounded-md border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadForm(false);
                  setSelectedFile(null);
                  setDescription("");
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDocumentUpload}
                disabled={uploading || !selectedFile}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? "Uploading..." : "Upload Document"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Documents List */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Documents</h4>
        {loading ? (
          <div className="text-center py-4">
            <div className="text-sm text-gray-500">Loading documents...</div>
          </div>
        ) : !Array.isArray(documents) || documents.length === 0 ? (
          <div className="text-center py-4">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <div className="text-sm text-gray-500">No documents uploaded yet</div>
          </div>
        ) : (
          <div className="space-y-2">
            {(Array.isArray(documents) ? documents : []).map((doc, index) => (
              <div key={index} className="bg-white p-3 rounded-md border border-gray-200 flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <FileText className="w-4 h-4 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{doc.filename || doc.name || `Document ${index + 1}`}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>{doc.type || 'Unknown Type'}</span>
                      {doc.size && <span>{formatFileSize(doc.size)}</span>}
                      {doc.uploadedAt && <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>}
                    </div>
                    {doc.description && (
                      <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {doc.url && (
                    <>
                      <button
                        onClick={() => window.open(doc.url, '_blank')}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="View document"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(doc.url, '_blank')}
                        className="p-1 text-gray-400 hover:text-green-600"
                        title="Download document"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDocumentDelete(doc.url || doc.fileUrl, doc.filename || doc.name)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Delete document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDocumentUploader;
